<template>
  <div v-if="visible" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">Agent Code QR</h3>
        <button @click="closeModal" class="modal-close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <div class="qr-container">
          <canvas ref="qrCanvas" class="qr-canvas"></canvas>
        </div>

        <div class="code-info">
          <div class="code-display">
            <span class="code-label">Agent Code:</span>
            <span class="code-value">{{ agentCode }}</span>
          </div>

          <button @click="copyToClipboard" class="copy-btn" :disabled="copying">
            <svg v-if="!copying" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
            </svg>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
            </svg>
            {{ copying ? 'Copied!' : 'Copy Code' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'

export default {
  name: 'QRCodeModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    agentCode: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      copying: false
    }
  },
  watch: {
    visible (newVal) {
      if (newVal && this.agentCode) {
        this.$nextTick(() => {
          this.generateQRCode()
        })
      }
    },
    agentCode (newVal) {
      if (this.visible && newVal) {
        this.$nextTick(() => {
          this.generateQRCode()
        })
      }
    }
  },
  methods: {
    generateQRCode () {
      if (this.$refs.qrCanvas && this.agentCode) {
        QRCode.toCanvas(this.$refs.qrCanvas, this.agentCode, {
          width: 200,
          margin: 2,
          color: {
            dark: '#1f2937',
            light: '#ffffff'
          }
        }, (error) => {
          if (error) {
            console.error('Error generating QR code:', error)
          }
        })
      }
    },
    async copyToClipboard () {
      try {
        this.copying = true
        await navigator.clipboard.writeText(this.agentCode)

        setTimeout(() => {
          this.copying = false
        }, 2000)
      } catch (error) {
        console.error('Failed to copy to clipboard:', error)
        this.copying = false

        // Fallback: create temporary textarea
        const textarea = document.createElement('textarea')
        textarea.value = this.agentCode
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
    },
    closeModal () {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--shadow-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px var(--shadow-primary), 0 10px 10px -5px var(--shadow-secondary);
  max-width: 400px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0;
  border-bottom: 1px solid var(--border-primary);
  margin-bottom: 24px;
  padding-bottom: 16px;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-body {
  padding: 0 24px 24px;
}

.qr-container {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  padding: 20px;
  background: var(--bg-secondary);
  border-radius: 12px;
  border: 2px dashed var(--border-secondary);
}

.qr-canvas {
  border-radius: 8px;
  background: var(--bg-primary);
  padding: 8px;
  box-shadow: 0 2px 8px var(--shadow-primary);
}

.code-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.code-display {
  flex: 1;
}

.code-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 4px;
}

.code-value {
  display: block;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Courier New', monospace;
  background: var(--bg-secondary);
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid var(--border-secondary);
}

.copy-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #3b82f6;
  color: white;
  border: none;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.copy-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.copy-btn:disabled {
  background: #10b981;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

@media (max-width: 480px) {
  .modal-content {
    width: 95%;
    margin: 20px;
  }

  .modal-header,
  .modal-body {
    padding-left: 16px;
    padding-right: 16px;
  }

  .code-info {
    flex-direction: column;
    align-items: stretch;
  }

  .copy-btn {
    justify-content: center;
  }
}
</style>
